/**
 * How the admin words a referral code (13.10.2026): its status and how far it
 * is from its number. The server decides both (`services/referrals/codes.py`).
 */
export const REFERRAL_STATUS = {
  active: { label: "În derulare", cls: "bg-[#3F8EBA]/15 text-[#1f567a]" },
  eligible: { label: "Beneficiu de dat", cls: "bg-[#519872]/20 text-[#1f6045] ring-2 ring-[#519872]/40" },
  delivered: { label: "Beneficiu dat", cls: "bg-[#373A45] text-white" },
  expired: { label: "Expirat", cls: "bg-[#EDEEF0] text-[#6B7280]" },
  cancelled: { label: "Anulat", cls: "bg-[#FDECEC] text-[#9b1f2c]" },
};

/**
 * `{ entered, paid, waiting, refunded, required }` for a code: who enrolled
 * with it, how many of them paid, how many have not yet, and how many of the
 * paid ones were refunded afterwards — those still count, flagged.
 */
export function referralProgress(group) {
  const members = Array.isArray(group?.members) ? group.members : [];
  const paid = members.filter((m) => m.is_valid).length;
  return {
    entered: members.length,
    paid,
    waiting: members.length - paid,
    refunded: members.filter((m) => m.is_valid && m.refunded_at).length,
    required: Number(group?.required ?? group?.offer?.required ?? 0) || 0,
  };
}

/** Romanian count: "un cursant", "3 cursanți", "20 de cursanți", "101 cursanți". */
function counted(n, one, many) {
  if (n === 1) return one;
  const tail = n % 100;
  return `${n}${n === 0 || tail === 0 || tail >= 20 ? " de" : ""} ${many}`;
}

export const participants = (n) => counted(n, "un cursant", "cursanți");
const days = (n) => counted(n, "o zi", "zile");
const notSent = (n) => (n === 1
  ? "Un email nu a plecat: rămâne în listă, îl poți trimite din nou."
  : `${counted(n, "", "emailuri")} nu au plecat: rămân în listă, le poți trimite din nou.`);

/** Under "Trimite codul celor deja înscriși": who paid and was never told of a code. */
export function waitingLine(count) {
  if (!count) return "Toți cursanții care și-au plătit locul au primit codul.";
  if (count === 1) return "Un cursant și-a plătit locul și nu a primit încă un cod.";
  return `${participants(count)} și-au plătit locul și nu au primit încă un cod.`;
}

/**
 * The question before sending. One press sends to at most `batch` people, so
 * a longer list is said as it is: the first of them now, the rest at the next
 * press.
 */
export function sendQuestion(count, expiresDays, batch = 100) {
  const term = `valabil ${days(expiresDays)} de azi`;
  if (count === 1) return `Trimiți codul de recomandare către un cursant? Primește un email cu codul lui, ${term}.`;
  if (count > batch) {
    return `Trimiți codul de recomandare către primii ${batch} din ${participants(count)}? `
      + `Fiecare primește un email cu codul lui, ${term}. Pentru ceilalți apeși din nou.`;
  }
  return `Trimiți codul de recomandare către ${participants(count)}? Fiecare primește un email cu codul lui, ${term}.`;
}

/** After sending: `{ tone: "ok" | "warn", text }` for what the server answered. */
export function sentLine(result) {
  const sent = Number(result?.sent) || 0;
  const failed = Number(result?.failed) || 0;
  const remaining = Number(result?.remaining) || 0;
  const parts = [sent ? `Am trimis codul către ${participants(sent)}.` : "Nu am trimis niciun cod."];
  if (failed) parts.push(notSent(failed));
  else if (remaining) parts.push(`Mai sunt ${participants(remaining)}: apasă din nou.`);
  return { tone: failed || !sent ? "warn" : "ok", text: parts.join(" ") };
}

/** What the team can do to a code in each state (`services/referrals/desk.py`). */
export function actionsFor(status) {
  return {
    deliver: status === "eligible",
    cancel: status === "active" || status === "expired" || status === "eligible",
    reopen: status === "delivered" || status === "cancelled",
  };
}

const SENT_VIA = {
  payment_email: "în emailul de confirmare a plății",
  code_email: "email separat, din fișa înscrierii",
  code_email_bulk: "email separat, către cei deja înscriși",
};

const EVENT_TITLES = {
  code_created: "Cod creat",
  group_created: "Cod creat",
  code_sent: "Cod trimis cursantului",
  member_joined: "Cineva s-a înscris cu acest cod",
  member_linked: "Înscriere legată de echipă",
  member_unlinked: "Legătura cu o înscriere a fost scoasă",
  member_paid: "O recomandare a fost plătită",
  member_refunded: "Plata unei recomandări a fost rambursată",
  status_changed: "Starea s-a schimbat",
  benefit_delivered: "Beneficiu acordat",
  code_cancelled: "Cod anulat",
  code_reopened: "Cod redeschis",
  team_notified: "Echipa a fost anunțată pe email",
};

/**
 * One line of a code's history, in words: `{ title, detail, by }`. `names`
 * maps an enrolment's id to the person's name, for the events about somebody.
 */
export function eventLine(event, names = {}) {
  const d = event?.details || {};
  const state = (s) => REFERRAL_STATUS[s]?.label || s || "";
  let detail = "";
  if (event?.type === "code_sent") detail = SENT_VIA[d.via] || "";
  else if (event?.type === "status_changed") detail = [state(d.from), state(d.to)].filter(Boolean).join(" → ");
  else if (event?.type === "benefit_delivered") detail = d.note || "";
  else if (event?.type === "code_cancelled") detail = d.reason ? `Motiv: ${d.reason}` : "";
  else if (event?.type === "code_reopened") detail = d.from ? `Era: ${state(d.from)}` : "";
  else if (d.enrollment_id) detail = names[d.enrollment_id] || "";
  const actor = event?.actor && event.actor !== "system" ? event.actor : "";
  return { title: EVENT_TITLES[event?.type] || event?.type || "", detail, by: actor };
}
